import GiftCard from "./GiftCard"
import type { Item } from "../types"
import { errorMessage } from "../types"
import { useState } from "react"
import ItemImage from "./ItemImage"

export default function ItemRow({
  item,
  onPurchase,
}: {
  item: Item
  onPurchase: (id: number) => Promise<unknown>
}) {
  const [confirming, setConfirming] = useState(false)
  const [purchasing, setPurchasing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const purchasedByMe = item.purchased && item.purchased_by !== undefined

  async function handleConfirm() {
    if (purchasing) return
    setError(null)
    setPurchasing(true)
    try {
      await onPurchase(item.id)
      setConfirming(false)
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setPurchasing(false)
    }
  }

  function renderPurchaseControls() {
    if (item.purchased) {
      return (
        <span
          className={`text-sm px-2 py-0.5 rounded-full ${
            purchasedByMe
              ? "bg-green-100 text-green-700"
              : "bg-gray-100 text-gray-700"
          }`}
        >
          {purchasedByMe ? "Purchased by you" : "Purchased"}
        </span>
      )
    }
    if (confirming) {
      return (
        <div className="flex flex-wrap items-center gap-2">
          <span className="min-h-11 min-w-11 px-3 inline-flex items-center text-sm text-gray-500">Mark this gift as purchased? The list owner won’t see this.</span>
          <button
            onClick={() => {
              void handleConfirm()
            }}
            disabled={purchasing}
            className="min-h-11 min-w-11 px-3 inline-flex items-center text-sm text-green-600 hover:text-green-800 font-medium disabled:opacity-50"
          >
            {purchasing ? "Marking as purchased…" : "Mark as purchased"}
          </button>
          <button
            onClick={() => {
              setConfirming(false)
              setError(null)
            }}
            disabled={purchasing}
            className="min-h-11 min-w-11 px-3 inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            Cancel
          </button>
        </div>
      )
    }
    return (
      <button
        onClick={() => setConfirming(true)}
        className="min-h-11 min-w-11 px-3 inline-flex items-center text-sm bg-blue-600 text-white px-3 py-1 rounded-full hover:bg-blue-700 transition-colors"
      >
        Mark as purchased
      </button>
    )
  }

  return (
    <GiftCard id={item.id} name={item.name}>
      <ItemImage src={item.image_url} size="card" />
      <div className="min-w-0 break-words">
        <h3
          className="text-base font-medium text-gray-900"
        >
          {item.name}
        </h3>

        {item.notes && (
          <p className=" text-base text-gray-600 mt-0.5 whitespace-pre-wrap break-words">{item.notes}</p>
        )}
        {error && (
          <p role="alert" className="min-h-11 min-w-11 px-3 inline-flex items-center text-sm text-red-600 mt-2">
            {error}
          </p>
        )}
      </div>

      <div className="col-span-2 flex flex-wrap items-center gap-2">
        {item.url && (
          <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="min-h-11 min-w-11 px-3 inline-flex items-center text-sm text-blue-600 hover:underline py-3"
          >
            View at store ↗
          </a>
        )}
        {renderPurchaseControls()}
      </div>
    </GiftCard>
  )
}
